import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UnitCardContainer, UnitCardDescription, UnitCardIconContainer, UnitCardTooltipContent,
} from './styles';
import { Button } from 'components';
import * as icons from 'icons';
import { IconNames } from '../../types';
import { colors } from 'styles/colors';
import ReactTooltip from 'react-tooltip';
import { InfoTooltip } from '../InfoTooltip';

interface UnitCardProps {
  cardColor: string;
  icon: {
    name: IconNames;
    color: string;
    size?: number;
  }
  description: string;
  /** Texto do ícone de informação ao lado da descrição; sem ele, o ícone não aparece. */
  tooltip?: string;
  value: number | undefined;
  onClickDetails: (() => void) | undefined;
  disabledReasonKey?: string;
}

interface UnitCardIconProps {
  color: string;
  icon: IconNames;
  disabled: boolean;
  size?: number;
}

export function UnitCard({
  cardColor,
  description,
  tooltip,
  icon,
  onClickDetails,
  value,
  disabledReasonKey,
}: Readonly<UnitCardProps>): ReactElement {
  const { t } = useTranslation();
  const disabledByValue = value === undefined;
  const hasDisabledReason = !!disabledReasonKey && !disabledByValue;
  const detailsDisabled = hasDisabledReason && !onClickDetails;

  const tooltipId = `unit-card-details-${icon.name}-${description}`;

  return (
    <UnitCardContainer
      backgroundColor={cardColor}
      disabledCard={disabledByValue}
    >
      <UnitCardIcon
        disabled={disabledByValue}
        icon={icon.name}
        color={icon.color}
        size={icon.size}
      />
      <h5>{value ?? '-'}</h5>
      <UnitCardDescription>
        <h6>{description}</h6>
        {tooltip ? (
          <InfoTooltip id={`unit-card-${icon.name}-${description}`}>
            <UnitCardTooltipContent>
              <span>{tooltip}</span>
            </UnitCardTooltipContent>
          </InfoTooltip>
        ) : null}
      </UnitCardDescription>

      {!disabledByValue && (onClickDetails || hasDisabledReason) && (
        <>
          {detailsDisabled && (
            <ReactTooltip
              id={tooltipId}
              place="top"
              type="dark"
              effect="solid"
              backgroundColor={colors.bg['surface-700']}
              className="tooltip-bignumber"
            >
              <span>{t(disabledReasonKey ?? '')}</span>
            </ReactTooltip>
          )}
          <Button
            onClick={detailsDisabled ? undefined : onClickDetails}
            type="button"
            variant="primary-ghost"
            style={{
              width: 105,
              backgroundColor: detailsDisabled ? 'transparent' : undefined,
              borderColor: detailsDisabled ? 'transparent' : undefined,
              boxShadow: detailsDisabled ? 'none' : undefined,
              cursor: detailsDisabled ? 'default' : undefined,
            }}
            data-tip={detailsDisabled ? true : undefined}
            data-for={detailsDisabled ? tooltipId : undefined}
          >
            {t('verDetalhes')}
          </Button>
        </>
      )}
    </UnitCardContainer>
  );
}

function UnitCardIcon({
  color,
  disabled,
  icon,
  size,
}: Readonly<UnitCardIconProps>): ReactElement {
  const Icon = icons[icon];

  return (
    <UnitCardIconContainer
      backgroundColor={color}
      disabled={disabled}
    >
      <Icon
        color={colors.icon['white-0']}
        size={size ?? 22}
      />
    </UnitCardIconContainer>
  );
}
